import {
  plugin as unleashPlugin,
  useUnleashContext,
  type IConfig,
} from '@unleash/proxy-client-vue';

// these are the constraints you'd set up in the Unleash dashboard
// for your feature flags.
// The keys here are the route params from our app,
// and the values are the context keys that Unleash expects.
const dynamicContextLookup = {
  channelId: 'studyId',
  memberId: 'memberId',
};

export default defineNuxtPlugin((nuxtApp) => {
  const appConfig = useRuntimeConfig();

  const environment =
    appConfig.public.unleash.token?.split(':')[1]?.split('.')?.[0] ??
    'development';

  const auth = useAuth();
  const route = useRoute();

  /**
   * Adds user data to the Unleash context.
   *
   * @param context The Unleash context object to which user data will be added.
   */
  function addUserDataToContext(context: Record<string, string>) {
    context.userId = auth.data.value?.user.sub || '';
    context.emailAddress = auth.data.value?.user.email || '';
  }

  /**
   * Sets the initial Unleash context based on
   * the current route parametersand user data.
   *
   * @returns The initial Unleash context object.
   */
  function setRouteAndAuthContexts() {
    const routeParams = route.params as Record<string, string>;
    const context = Object.keys(dynamicContextLookup).reduce(
      (acc, key) => {
        if (routeParams[key]) {
          acc[dynamicContextLookup[key as keyof typeof dynamicContextLookup]] =
            routeParams[key];
        }
        return acc;
      },
      {} as Record<string, string>
    );

    addUserDataToContext(context);

    return context;
  }

  const config: IConfig = {
    url: appConfig.public.unleash.url,
    clientKey: appConfig.public.unleash.token,
    refreshInterval: 60, // 60 seconds refresh
    appName: appConfig.public.emsere.appName,
    environment,
    context: {
      // since we got rid of immediate context update on watcher to
      // prevent double requests, we gotta set initial context here
      ...setRouteAndAuthContexts(),
    },
  };
  nuxtApp.vueApp.use(unleashPlugin, { config });

  // @unleash/proxy-client-vue is a client-side SDK; skip the watcher on the server
  if (import.meta.server) {
    return;
  }

  const updateContext = useUnleashContext();

  watch(
    () => {
      return setRouteAndAuthContexts();
    },
    (newContext, oldContext) => {
      if (!updateContext) return;

      // Only call if values actually changed, not just reference.
      // This prunes out 60s refresh from auth as well.
      const changed =
        Object.keys(newContext).some(
          (k) => newContext[k] !== oldContext?.[k]
        ) ||
        Object.keys(oldContext ?? {}).some(
          (k) => newContext[k] !== oldContext?.[k]
        );
      if (!changed) return;
      updateContext(newContext);
    },
    { deep: true }
  );
});
